import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { TextField, MenuItem, Grid, FormControlLabel, Switch, Button, Typography, CircularProgress, Box } from '@material-ui/core';
import InputMask from 'react-input-mask';

import { getNegociosAtivos } from '../../store/actions/negocio';
import { addCupomDeDesconto, getCupomDeDesconto, editCupomDeDesconto } from '../../store/actions/cupomDeDesconto';
import TituloPagina from '../../components/layout/TituloPagina';
import LoadingOverlay from '../../components/layout/LoadingOverlay';
import If from '../../components/condicional/if';

const CupomDeDescontoCadastro = props => {
    const history = useHistory();
    const location = useLocation();
    const { addCupomDeDesconto, getNegociosAtivos, negocios, loading, processing, user, getCupomDeDesconto, editCupomDeDesconto, cupom } = props;
    const { perfis, funcionarios } = user || {};
    const [disableFields, setDisableFields] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [processingMessage, setProcessingMessage] = useState('');
    const isPlatformAdmin = Array.isArray(perfis) && perfis.includes('ADMIN_APP');
    const userStoreId = Array.isArray(funcionarios) && funcionarios.length > 0 ? funcionarios[0].idNegocio : null;

    const id = Number(location?.state?.cupomId);
    const isEditRoute = location.pathname === '/cupom-de-desconto/editar-cupom';
    const isDetailRoute = location.pathname === '/cupom-de-desconto/detalhar-cupom';
    
    useEffect(() => {

        if ((isEditRoute && !id) || (isDetailRoute && !id)) {
            history.replace('/cupom-de-desconto');
            return;
        }

        if (id > 0) {
            getCupomDeDesconto(id);
            if ((isEditRoute || isDetailRoute) && isPlatformAdmin) {
                getNegociosAtivos();
            }

            if (isDetailRoute) {
                setDisableFields(true);
            }
        }
    }, [getCupomDeDesconto, getNegociosAtivos, history, location]);

    const validationSchema = yup.object().shape({
        // 1. Código do Cupom: Sempre em letras maiúsculas, sem espaços ou caracteres especiais
        codigo: yup.string()
            .transform((value) => value.trim().toUpperCase())
            .required('O código do cupom é obrigatório')
            .min(3, 'O código deve ter pelo menos 3 caracteres')
            .max(30, 'O código deve ter no máximo 30 caracteres')
            .matches(/^[A-Z0-9]+$/, 'O código não deve conter espaços ou caracteres especiais'),

        // 2. Tipo de Desconto
        tipoDeDesconto: yup.string()
            .oneOf(['PORCENTAGEM', 'VALOR_FIXO'], 'Tipo de desconto inválido')
            .required('O tipo de desconto é obrigatório'),

        // 3. Valor do Desconto: Validação dinâmica com base no tipo
        valorDoDesconto: yup.number()
            .typeError('O valor deve ser um número')
            .required('O valor do desconto é obrigatório')
            .positive('O valor deve ser maior que zero')
            .when('tipoDeDesconto', {
                is: 'PORCENTAGEM',
                then: (schema) => schema.max(100, 'Para porcentagem, o valor máximo é 100%'),
                otherwise: (schema) => schema.max(99999, 'Valor fixo muito alto'),
            }),

        // 4. Teto de Desconto: Só faz sentido se for porcentagem
        maxValorDoDesconto: yup.number()
            .typeError('O valor deve ser um número')
            .nullable()
            .transform((value, originalValue) => originalValue === '' ? null : value)
            .positive('O teto de desconto deve ser maior que zero'),

        // 5. Valor Mínimo do Pedido
        minValorPedido: yup.number()
            .typeError('O valor deve ser um número')
            .required('O valor mínimo do pedido é obrigatório')
            .min(0, 'O valor mínimo não pode ser negativo'),

        // 6. Escopo do Cupom
        escopo: yup.string()
            .oneOf(['GLOBAL', 'NEGOCIO_ESPECIFICO'], 'Escopo inválido')
            .required('O escopo é obrigatório'),

        // 7. ID da Loja: Obrigatório apenas se o escopo for de Loja E se for o Admin criando
        idNegocio: yup.mixed()
            .when('escopo', {
                is: 'NEGOCIO_ESPECIFICO',
                then: (schema) => schema.required('É necessário selecionar ou informar a loja para este cupom'),
                otherwise: (schema) => schema.nullable(),
            }),

        // 8. Limites de Uso
        limiteDeUso: yup.number()
            .typeError('Deve ser um número inteiro')
            .required('Informe a quantidade de vezes total que este cupom pode ser utilizado')
            .integer('Deve ser um número inteiro')
            .positive('O limite deve ser maior que zero'),

        limiteDeUsoPorUsuario: yup.number()
            .typeError('Deve ser um número inteiro')
            .required('Informe a quantidade de vezes que um mesmo usuário pode utilizar este cupom')
            .integer('Deve ser um número inteiro')
            .positive('O limite deve ser maior que zero'),

        // 9. Datas de Vigência: Validação cronológica impeditiva
        dataInicio: yup.string()
            .required('A data de início é obrigatória')
            .test('data-valida', 'Data de início inválida (formato: dd/mm/aaaa)', (value) => {
                if (!value) return false;
                const [day, month, year] = value.split('/');
                if (!day || !month || !year || day.length !== 2 || month.length !== 2 || year.length !== 4) {
                    return false;
                }
                const data = new Date(year, month - 1, day);
                return !isNaN(data.getTime());
            })
            .test('data-nao-passado', 'A data de início não pode ser no passado', (value) => {
                if (!value) return false;
                const [day, month, year] = value.split('/');
                const data = new Date(year, month - 1, day);
                const hoje = new Date(new Date().setHours(0, 0, 0, 0));
                return data >= hoje;
            }),

        dataFim: yup.string()
            .required('A data de término é obrigatória')
            .test('data-valida', 'Data de término inválida (formato: dd/mm/aaaa)', (value) => {
                if (!value) return false;
                const [day, month, year] = value.split('/');
                if (!day || !month || !year || day.length !== 2 || month.length !== 2 || year.length !== 4) {
                    return false;
                }
                const data = new Date(year, month - 1, day);
                return !isNaN(data.getTime());
            })
            .test('data-posterior-inicio', 'A data de término deve ser posterior à data de início', function (value) {
                if (!value || !this.parent.dataInicio) return true;
                const [dayFim, monthFim, yearFim] = value.split('/');
                const [dayInicio, monthInicio, yearInicio] = this.parent.dataInicio.split('/');
                const dataFim = new Date(yearFim, monthFim - 1, dayFim);
                const dataInicio = new Date(yearInicio, monthInicio - 1, dayInicio);
                return dataFim > dataInicio;
            }),
    });

    const formik = useFormik({
        initialValues: {
            id: cupom?.id || '',
            codigo: cupom?.codigo || '',
            descricao: cupom?.descricao || '',
            tipoDeDesconto: cupom?.tipoDeDesconto || '',
            valorDoDesconto: cupom?.valorDoDesconto || '',
            minValorPedido: cupom?.minValorPedido || '',
            maxValorDoDesconto: cupom?.maxValorDoDesconto || '',
            escopo: (isEditRoute || isDetailRoute) ? cupom?.escopo : (isPlatformAdmin ? 'GLOBAL' : 'NEGOCIO_ESPECIFICO'),
            limiteDeUso: cupom?.limiteDeUso || '',
            limiteDeUsoPorUsuario: cupom?.limiteDeUsoPorUsuario || '',
            dataInicio: cupom?.dataInicio || '',
            dataFim: cupom?.dataFim || '',
            ativo: cupom?.ativo || false,
            idNegocio: (isEditRoute || isDetailRoute) ? cupom?.idNegocio : (isPlatformAdmin ? '' : userStoreId)
        },
        validationSchema: validationSchema,
        onSubmit: (data) => {
            setProcessingMessage('Salvando...');

            if (isEditRoute) {
                editCupomDeDesconto(data);
            } else {
                addCupomDeDesconto(data);
            }

            setDisableFields(true);
            setProcessingMessage('');
        },
        enableReinitialize: true,
    });

    const handleEscopoChange = (value) => {
        if (value === 'GLOBAL') {
            formik.setFieldValue('idNegocio', '');
        } else if (value === 'NEGOCIO_ESPECIFICO') {
            getNegociosAtivos();
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, height: '70vh' }}>
                <CircularProgress size={48} />
            </Box>
        );
    }

    return (
        <>
            <TituloPagina titulo='Cadastro de Cupom de Desconto' subtitulo={new Date().toLocaleString('pt-BR')} />
            <Grid container item xs={12} style={{ paddingTop: '15px' }}>
                <Grid container>
                    <Typography variant='body1' component='p'>
                        Informe abaixo os dados do cupom de desconto.
                    </Typography>
                </Grid>
                <form onSubmit={formik.handleSubmit} noValidate autoComplete='off'>
                    <div style={{ flexGrow: 1, paddingTop: '15px' }}>
                        <Grid container spacing={2}>
                            {isPlatformAdmin && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            fullWidth
                                            id='escopo'
                                            name='escopo'
                                            label='Escopo do Cupom de Desconto'
                                            variant='filled'
                                            disabled={disableFields}
                                            value={formik.values.escopo}
                                            onChange={(event) => {
                                                formik.handleChange(event);
                                                handleEscopoChange(event.target.value);
                                            }}
                                            error={formik.touched.escopo && Boolean(formik.errors.escopo)}
                                            helperText={formik.touched.escopo && formik.errors.escopo}>
                                            <MenuItem key='0' value=''>Selecione o escopo do cupom de desconto</MenuItem>
                                            <MenuItem key='1' value='GLOBAL'>Global</MenuItem>
                                            <MenuItem key='2' value='NEGOCIO_ESPECIFICO'>Negócio Específico</MenuItem>
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            select
                                            fullWidth
                                            id='idNegocio'
                                            name='idNegocio'
                                            label='Selecione o Negócio'
                                            variant='filled'
                                            disabled={disableFields || formik.values.escopo === 'GLOBAL'}
                                            value={formik.values.idNegocio}
                                            onChange={formik.handleChange}
                                            error={formik.touched.idNegocio && Boolean(formik.errors.idNegocio)}
                                            helperText={formik.touched.idNegocio && formik.errors.idNegocio}>
                                            <MenuItem key='0' value=''>Selecione o negócio</MenuItem>
                                            {negocios?.map((negocio) => (
                                                <MenuItem key={negocio.id} value={negocio.id}>
                                                    {negocio.nome}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                </>
                            )}
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='codigo'
                                    name='codigo'
                                    label='Código do Cupom (Ex: DIADAMAES10OFF)'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.codigo}
                                    onChange={formik.handleChange}
                                    error={formik.touched.codigo && Boolean(formik.errors.codigo)}
                                    helperText={formik.touched.codigo && formik.errors.codigo} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <InputMask
                                    mask={'99/99/9999'}
                                    maskChar=' '
                                    disabled={disableFields}
                                    value={formik.values.dataInicio}
                                    onChange={formik.handleChange}>
                                    {() => <TextField
                                        fullWidth
                                        id='dataInicio'
                                        name='dataInicio'
                                        label='Data de Início da Vigência'
                                        variant='filled'
                                        error={formik.touched.dataInicio && Boolean(formik.errors.dataInicio)}
                                        helperText={formik.touched.dataInicio && formik.errors.dataInicio}
                                    />}
                                </InputMask>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <InputMask
                                    mask={'99/99/9999'}
                                    maskChar=' '
                                    disabled={disableFields}
                                    value={formik.values.dataFim}
                                    onChange={formik.handleChange}>
                                    {() => <TextField
                                        fullWidth
                                        id='dataFim'
                                        name='dataFim'
                                        label='Data de Término da Vigência'
                                        variant='filled'
                                        error={formik.touched.dataFim && Boolean(formik.errors.dataFim)}
                                        helperText={formik.touched.dataFim && formik.errors.dataFim}
                                    />}
                                </InputMask>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    id="descricao"
                                    name="descricao"
                                    label="Breve descrição do cupom (opcional)"
                                    variant="filled"
                                    multiline
                                    rows={3}
                                    disabled={disableFields}
                                    value={formik.values.descricao}
                                    onChange={formik.handleChange}
                                    error={formik.touched.descricao && Boolean(formik.errors.descricao)}
                                    helperText={formik.touched.descricao && formik.errors.descricao}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    select
                                    fullWidth
                                    id='tipoDeDesconto'
                                    name='tipoDeDesconto'
                                    label='Tipo de Desconto'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.tipoDeDesconto}
                                    onChange={formik.handleChange}
                                    error={formik.touched.tipoDeDesconto && Boolean(formik.errors.tipoDeDesconto)}
                                    helperText={formik.touched.tipoDeDesconto && formik.errors.tipoDeDesconto}>
                                    <MenuItem key='0' value=''>Selecione o tipo de desconto</MenuItem>
                                    <MenuItem key='1' value='PORCENTAGEM'>Porcentagem (%)</MenuItem>
                                    <MenuItem key='2' value='VALOR_FIXO'>Valor Fixo (R$)</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='valorDoDesconto'
                                    name='valorDoDesconto'
                                    label='Valor do Desconto'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.valorDoDesconto}
                                    onChange={formik.handleChange}
                                    error={formik.touched.valorDoDesconto && Boolean(formik.errors.valorDoDesconto)}
                                    helperText={formik.touched.valorDoDesconto && formik.errors.valorDoDesconto}>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='maxValorDoDesconto'
                                    name='maxValorDoDesconto'
                                    label='Valor Máximo em R$ do Desconto'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.maxValorDoDesconto}
                                    onChange={formik.handleChange}
                                    error={formik.touched.maxValorDoDesconto && Boolean(formik.errors.maxValorDoDesconto)}
                                    helperText={formik.touched.maxValorDoDesconto && formik.errors.maxValorDoDesconto}>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='minValorPedido'
                                    name='minValorPedido'
                                    label='Valor Mínimo do Pedido'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.minValorPedido}
                                    onChange={formik.handleChange}
                                    error={formik.touched.minValorPedido && Boolean(formik.errors.minValorPedido)}
                                    helperText={formik.touched.minValorPedido && formik.errors.minValorPedido}>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='limiteDeUso'
                                    name='limiteDeUso'
                                    label='Limite de Uso Global'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.limiteDeUso}
                                    onChange={formik.handleChange}
                                    error={formik.touched.limiteDeUso && Boolean(formik.errors.limiteDeUso)}
                                    helperText={formik.touched.limiteDeUso && formik.errors.limiteDeUso}>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    id='limiteDeUsoPorUsuario'
                                    name='limiteDeUsoPorUsuario'
                                    label='Limite de Uso por Usuário'
                                    variant='filled'
                                    disabled={disableFields}
                                    value={formik.values.limiteDeUsoPorUsuario}
                                    onChange={formik.handleChange}
                                    error={formik.touched.limiteDeUsoPorUsuario && Boolean(formik.errors.limiteDeUsoPorUsuario)}
                                    helperText={formik.touched.limiteDeUsoPorUsuario && formik.errors.limiteDeUsoPorUsuario}>
                                </TextField>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={<Switch
                                        checked={formik.values.ativo}
                                        onChange={formik.handleChange}
                                        name="ativo"
                                        disabled={disableFields} />}
                                    label="Cupom Ativo"
                                />
                            </Grid>

                        </Grid>
                        <Grid container pt={4} style={{ paddingTop: '25px', paddingBottom: '25px' }}>
                            <If test={!disableFields}>
                                <Grid item style={{ paddingRight: '15px' }}>
                                    <Button type='submit' color='secondary' variant='contained'>Salvar</Button>
                                </Grid>
                                <Grid item style={{ paddingRight: '15px' }}>
                                    <Button variant='contained' href='/cupom-de-desconto'>Cancelar</Button>
                                </Grid>
                            </If>
                            <If test={disableFields}>
                                <Grid item style={{ paddingRight: '15px' }}>
                                    <Button color='secondary' variant='contained' href='/cupom-de-desconto'>Voltar</Button>
                                </Grid>
                            </If>
                        </Grid>
                    </div>
                </form>
            </Grid>

            {/* O componente Loading fica ouvindo o estado isProcessing */}
            <LoadingOverlay
                open={isProcessing || processing}
                message={processingMessage}
            />

        </>
    )
}

const mapStateToProps = state => ({
    loading: state.cupomDeDesconto.loading,
    processing: state.cupomDeDesconto.processing,
    user: state.auth.user,
    negocios: state.negocio.negocios,
    cupom: state.cupomDeDesconto.cupom
})
const mapDispatchToProps = dispatch => bindActionCreators({
    addCupomDeDesconto,
    editCupomDeDesconto,
    getCupomDeDesconto,
    getNegociosAtivos
}, dispatch)

export default connect(mapStateToProps, mapDispatchToProps)(CupomDeDescontoCadastro)